import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import type { FeePayerStat } from "../../lib/api";
import { Input } from "../ui";

/**
 * Fee payer picker: the top payers by tx count as suggestions, but the field
 * is a real input — any address can be typed or pasted and committed with
 * Enter. A native select can't do the paste half, hence the combobox.
 */

const Wrapper = styled.div`
  position: relative;
  flex: 1 1 220px;
  min-width: 180px;
  max-width: 340px;
`;

const BoxInput = styled(Input)`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.xs};
  padding-right: 26px; /* room for the clear button */
`;

const ClearButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};
  line-height: 1;

  &:hover {
    color: ${theme.colors.text};
  }
`;

const Results = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  margin: 0;
  padding: ${theme.spacing.xs};
  list-style: none;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  max-height: 40vh;
  overflow-y: auto;
`;

const Row = styled.li<{ active: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  padding: 6px 8px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.fontSize.xs};
  cursor: pointer;
  background: ${(p) => (p.active ? theme.colors.bgHover : "transparent")};
`;

const RowLabel = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowCount = styled.span`
  margin-left: auto;
  padding-left: ${theme.spacing.sm};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
`;

const Hint = styled.li`
  padding: 6px 8px;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const truncateAddress = (addr: string) =>
  addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr;

export interface FeePayerComboboxProps {
  value: string | undefined;
  feePayers: FeePayerStat[] | undefined;
  onChange: (address: string | undefined) => void;
}

export function FeePayerCombobox({ value, feePayers, onChange }: FeePayerComboboxProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // Re-seed on external changes: a removed chip, back/forward, a link.
  useEffect(() => {
    setText(value ?? "");
  }, [value]);

  // Filter suggestions by what's typed — except when the field still shows
  // the committed value, where the user is browsing, not narrowing.
  const suggestions = useMemo(() => {
    const all = feePayers ?? [];
    const query = text.trim().toLowerCase();
    if (!query || query === value?.toLowerCase()) return all;
    return all.filter(
      (p) => p.address.toLowerCase().includes(query) || p.label?.toLowerCase().includes(query),
    );
  }, [feePayers, text, value]);

  useEffect(() => {
    setActive(0);
  }, [text]);

  // Click-away closes without committing a half-typed address.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setText(value ?? "");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, value]);

  const commit = (address: string | undefined) => {
    setOpen(false);
    onChange(address || undefined);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && suggestions[active]) commit(suggestions[active].address);
      else commit(text.trim());
    } else if (e.key === "Escape") {
      setOpen(false);
      setText(value ?? "");
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <BoxInput
        type="text"
        placeholder="Fee payer: any"
        value={text}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {value && !open && (
        <ClearButton onClick={() => commit(undefined)} title="Clear fee payer filter">
          ×
        </ClearButton>
      )}
      {open && (
        <Results>
          {suggestions.map((p, i) => (
            <Row
              key={p.address}
              active={i === active}
              onMouseEnter={() => setActive(i)}
              // mousedown, not click: the click-away listener runs on mousedown
              // and would close the list before a click could land.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(p.address);
              }}
            >
              <RowLabel>{p.label ?? truncateAddress(p.address)}</RowLabel>
              <RowCount>{p.count.toLocaleString()} txs</RowCount>
            </Row>
          ))}
          {suggestions.length === 0 && <Hint>No matching top payer — Enter filters by the pasted address</Hint>}
        </Results>
      )}
    </Wrapper>
  );
}
